/**
 * Drill-down inline de proyectos SGR históricos del municipio.
 *
 * Visible bajo el ScoreHero. Refuerza la narrativa del Score Alpha
 * mostrando concretamente que proyectos componen el monto captado.
 *
 * Estados:
 * - Loading: skeleton durante el fetch
 * - Empty (0 proyectos): mensaje neutral
 * - Con proyectos: tabla con summary expandible
 *   - Default OPEN si <= 10 proyectos
 *   - Default CLOSED si > 10 proyectos
 *
 * Sub-sprint #24 — Sprint Pre-Demo Alcaldesa Fase 2.
 *
 * En la ficha de otro municipio (`municipioAjeno`, issue #746) los textos
 * lo nombran en vez de hablar de "tu" captación.
 */

import { useQuery } from "@tanstack/react-query";

import { NombreMunicipio } from "@/components/NombreMunicipio";
import { track } from "@/lib/analytics";
import {
  fetchMunicipalitySgrProjects,
  type SgrHistoricalProjectDTO,
} from "@/lib/api/municipality-score";

import { bigIntFromCop, formatCop } from "./_shared";

const SECTOR_COLOR_MAP: Record<string, { bg: string; text: string }> = {
  VIVIENDA: { bg: "bg-blue-100", text: "text-blue-800" },
  AGRICULTURA: { bg: "bg-amber-100", text: "text-amber-800" },
  EDUCACION: { bg: "bg-violet-100", text: "text-violet-800" },
  SALUD: { bg: "bg-pink-100", text: "text-pink-800" },
  VIAS: { bg: "bg-slate-100", text: "text-slate-800" },
  ENERGIA: { bg: "bg-yellow-100", text: "text-yellow-800" },
  AGUA: { bg: "bg-cyan-100", text: "text-cyan-800" },
};

function sectorColors(sector: string): { bg: string; text: string } {
  const key = (sector.split(/[, ]/)[0] ?? "").toUpperCase();
  return SECTOR_COLOR_MAP[key] ?? { bg: "bg-slate-100", text: "text-slate-700" };
}

/**
 * Estilo de la pill de estado SGR del proyecto.
 *
 * Enum real del SGR: TERMINADO, EN EJECUCIÓN, DESAPROBADO.
 * (DESAPROBADO no debería llegar acá — el backend filtra esos rows
 * por R-SEC del PR #297. Lo dejamos como fallback gris por defensa.)
 */
function statusBadge(status: string | null): {
  label: string;
  bg: string;
  text: string;
} {
  const normalized = (status ?? "").toUpperCase();
  if (normalized === "TERMINADO") {
    return { label: "Terminado", bg: "bg-emerald-100", text: "text-emerald-800" };
  }
  if (normalized === "EN EJECUCIÓN" || normalized === "EN EJECUCION") {
    return { label: "En ejecución", bg: "bg-blue-100", text: "text-blue-800" };
  }
  return { label: status ?? "—", bg: "bg-slate-100", text: "text-slate-700" };
}

export function ProjectsSgrSection({
  dane,
  captacionTotalCop,
  municipioAjeno,
}: {
  dane: string;
  captacionTotalCop: string;
  /** Nombre del municipio de la ficha cuando no es el del usuario; null en la propia. */
  municipioAjeno: string | null;
}) {
  const { data: projects, isLoading } = useQuery({
    queryKey: ["municipality-sgr-projects", dane],
    queryFn: () => fetchMunicipalitySgrProjects(dane),
    staleTime: 60 * 60 * 1000, // 1h
  });

  if (isLoading) {
    return (
      <section
        data-testid="ficha-sgr-projects-skeleton"
        className="mt-6 h-32 animate-pulse rounded-2xl bg-slate-100"
      />
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <section
        data-testid="ficha-sgr-projects-empty"
        className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4"
      >
        <p className="text-sm text-slate-600">
          {municipioAjeno ? <NombreMunicipio nombre={municipioAjeno} /> : "Tu municipio"} no tiene
          proyectos SGR históricos registrados.
        </p>
      </section>
    );
  }

  const defaultOpen = projects.length <= 10;
  const captacionFormatted = formatCop(bigIntFromCop(captacionTotalCop));

  return (
    <details
      data-testid="ficha-sgr-projects-details"
      open={defaultOpen ? true : undefined}
      onToggle={(e) => {
        // Sprint Pre-Demo: telemetría del drill-down. Se dispara tanto en
        // click del usuario como en el toggle inicial cuando `defaultOpen`
        // es true (los <=10 proyectos arrancan abiertos) — eso refleja
        // que el funcionario lo VIO abierto, dato útil para entender
        // engagement con la sección.
        const newState = (e.currentTarget as HTMLDetailsElement).open ? "open" : "closed";
        track({
          event_name: "ficha_sgr_projects_expanded",
          properties: {
            dane_code: dane,
            project_count: projects.length,
            captacion_total_cop: captacionTotalCop,
            starts_open: defaultOpen,
            new_state: newState,
          },
        });
      }}
      className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-xs"
    >
      <summary className="cursor-pointer px-6 py-4 select-none">
        <span className="text-xs font-semibold tracking-wider text-slate-600 uppercase">
          {/* El resumen va en mayúsculas (CSS), así que el nombre no necesita formato. */}
          {`Proyectos que componen ${
            municipioAjeno ? `la captación de ${municipioAjeno}` : "tu captación"
          } (${captacionFormatted})`}
        </span>
        <span className="ml-2 text-xs text-slate-400">
          · {projects.length} proyecto{projects.length === 1 ? "" : "s"}
        </span>
      </summary>
      <div className="overflow-x-auto px-6 pb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-2 py-2 text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                Estado
              </th>
              <th className="px-2 py-2 text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                Sector
              </th>
              <th className="px-2 py-2 text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                Proyecto
              </th>
              <th className="px-2 py-2 text-right text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                Monto
              </th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p: SgrHistoricalProjectDTO) => {
              const colors = sectorColors(p.sector);
              const status = statusBadge(p.status);
              return (
                <tr
                  key={p.bpin}
                  data-testid="ficha-sgr-project-row"
                  className="border-b border-slate-100 last:border-b-0"
                >
                  <td className="px-2 py-2">
                    <span
                      className={`rounded-full ${status.bg} px-2 py-0.5 text-[10px] font-semibold ${status.text}`}
                      data-testid="ficha-sgr-project-status"
                    >
                      {status.label}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={`rounded-full ${colors.bg} px-2 py-0.5 text-[10px] font-semibold uppercase ${colors.text}`}
                    >
                      {p.sector.split(/[, ]/)[0]}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-slate-900" title={p.name}>
                    {p.name.length > 60 ? p.name.slice(0, 60) + "…" : p.name}
                  </td>
                  <td className="px-2 py-2 text-right font-semibold text-slate-900">
                    {formatCop(bigIntFromCop(p.amount_approved_cop))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}
